import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import {
  Plus,
  ArrowLeft,
  FileText,
  Loader2,
  Trash2,
  Search,
  AlertTriangle,
  Layers,
  Send,
  Upload,
  X,
  Check,
} from 'lucide-react';
import * as api from '../lib/esignApi';
import { useAuth } from '../hooks/useAuth';
import SignInRequired from '../components/SignInRequired';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function EsignTemplates() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [cap, setCap] = useState(5);
  const [used, setUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [useModal, setUseModal] = useState(null); // { template, roles, fields }
  const [bulkModal, setBulkModal] = useState(null); // { template, roles, fields }
  const [deleteModal, setDeleteModal] = useState(null); // template
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);

  const load = useCallback(
    async (q = '') => {
      try {
        setError(null);
        const data = await api.listEnvelopeTemplates(q);
        setItems(data.items || []);
        setCap(data.cap ?? 5);
        setUsed(data.used ?? (data.items || []).length);
      } catch (err) {
        setError(err.message);
      }
    },
    []
  );

  useEffect(() => {
    if (authLoading || !user) return;
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load, authLoading, user]);

  useEffect(() => {
    if (!toast) return;
    // Errors stick until dismissed; success/warn auto-clear after 5s.
    if (toast.kind === 'error') return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search, load]);

  const handleUse = useCallback(async (template) => {
    try {
      const detail = await api.getEnvelopeTemplate(template.id);
      setUseModal({ template: detail.template, roles: detail.roles, fields: detail.fields });
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    }
  }, []);

  const handleBulk = useCallback(async (template) => {
    try {
      const detail = await api.getEnvelopeTemplate(template.id);
      setBulkModal({ template: detail.template, roles: detail.roles, fields: detail.fields });
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    }
  }, []);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!deleteModal) return;
    try {
      await api.deleteEnvelopeTemplate(deleteModal.id);
      setDeleteModal(null);
      setToast({ kind: 'success', text: 'Template deleted.' });
      await load(search.trim());
    } catch (err) {
      setToast({ kind: 'error', text: err.message });
    }
  }, [deleteModal, load, search]);

  const atCap = used >= cap;

  // Logged-out (e.g. a licensed desktop user who signed out): eSign needs a
  // live session — prompt sign-in instead of a dead page.
  if (!authLoading && !user) {
    return <SignInRequired feature="eSign" />;
  }

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto">
      <Link
        to="/esign"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition mb-5"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to envelopes
      </Link>

      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
          <p className="text-sm text-gray-500 mt-1">
            Reusable envelopes with role placeholders. {used} / {cap} used.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`my-4 p-3 rounded-lg text-sm border flex items-start justify-between gap-3 ${
            toast.kind === 'error'
              ? 'bg-red-50 border-red-200 text-red-700'
              : toast.kind === 'warn'
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-green-50 border-green-200 text-green-700'
          }`}
        >
          <span className="flex-1 min-w-0">{toast.text}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="shrink-0 opacity-60 hover:opacity-100"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="my-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3 mt-5 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates…"
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => setNewTemplateOpen(true)}
          disabled={atCap}
          title={atCap ? `You're at the ${cap}-template limit. Delete one to create another.` : undefined}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" />
          New Template
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-1">No templates yet</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Send an envelope, then click "Save as template" on its detail page to turn it into a
            reusable starting point for future sends.
          </p>
          <button
            onClick={() => navigate('/esign')}
            className="bg-indigo-600 text-white px-5 py-2.5 rounded-lg hover:bg-indigo-700 transition text-sm"
          >
            Go to envelopes
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {items.map((tpl) => (
              <li key={tpl.id} className="p-4 flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-500 shrink-0" />
                    <h3 className="text-sm font-semibold text-gray-900 truncate">{tpl.name}</h3>
                  </div>
                  {tpl.description && (
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{tpl.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {(tpl.roles || []).map((role) => (
                      <span
                        key={role.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700"
                      >
                        {role.role_label}
                        {role.role_kind !== 'signer' && (
                          <span className="text-gray-400">({role.role_kind})</span>
                        )}
                      </span>
                    ))}
                    <span className="text-xs text-gray-400">
                      {tpl.original_page_count} page{tpl.original_page_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => handleUse(tpl)}
                    className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition text-xs flex items-center gap-1.5"
                  >
                    <Send className="w-3 h-3" />
                    Use
                  </button>
                  <button
                    onClick={() => handleBulk(tpl)}
                    className="border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition text-xs flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3" />
                    Bulk send
                  </button>
                  <button
                    onClick={() => setDeleteModal(tpl)}
                    className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition"
                    title="Delete template"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {useModal && (
        <UseTemplateModal
          template={useModal.template}
          roles={useModal.roles}
          onClose={() => setUseModal(null)}
          onDone={(envelopeId) => {
            setUseModal(null);
            navigate(`/esign/${envelopeId}`);
          }}
          onError={(msg) => setToast({ kind: 'error', text: msg })}
        />
      )}

      {bulkModal && (
        <BulkSendModal
          template={bulkModal.template}
          roles={bulkModal.roles}
          onClose={() => setBulkModal(null)}
          onDone={(result) => {
            setBulkModal(null);
            const count = result.envelope_count;
            const failed = result.invites_failed || 0;
            setToast({
              kind: failed > 0 ? 'warn' : 'success',
              text: failed > 0
                ? `Sent ${count} envelope${count === 1 ? '' : 's'} (${failed} invite${failed === 1 ? '' : 's'} failed — resend from the envelope detail page).`
                : `Sent ${count} envelope${count === 1 ? '' : 's'}.`,
            });
            navigate('/esign');
          }}
          onError={(msg) => setToast({ kind: 'error', text: msg })}
        />
      )}

      {deleteModal && (
        <ConfirmDeleteModal
          name={deleteModal.name}
          onCancel={() => setDeleteModal(null)}
          onConfirm={handleDeleteConfirmed}
        />
      )}

      {newTemplateOpen && (
        <NewTemplateModal
          onClose={() => setNewTemplateOpen(false)}
          onCreated={async (templateName) => {
            setNewTemplateOpen(false);
            setToast({ kind: 'success', text: `Template "${templateName}" saved.` });
            await load(search.trim());
          }}
          onError={(msg) => setToast({ kind: 'error', text: msg })}
          onGoToEnvelopes={() => {
            setNewTemplateOpen(false);
            navigate('/esign');
          }}
        />
      )}
    </div>
  );
}

// ─── Use Template modal ─────────────────────────────────────────────────────

function UseTemplateModal({ template, roles, onClose, onDone, onError }) {
  const [recipients, setRecipients] = useState(() =>
    roles.map((r) => ({ role_id: r.id, role_label: r.role_label, name: '', email: '' }))
  );
  const [subject, setSubject] = useState(template.subject_template || template.name);
  const [message, setMessage] = useState(template.message_template || '');
  const [saving, setSaving] = useState(false);

  const canSubmit = useMemo(() => {
    return recipients.every((r) => r.name.trim() && EMAIL_RE.test(r.email));
  }, [recipients]);

  const handleChange = (index, key, value) => {
    setRecipients((prev) => {
      const next = prev.slice();
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const result = await api.instantiateEnvelopeTemplate(template.id, {
        recipients: recipients.map((r) => ({ role_id: r.role_id, name: r.name, email: r.email })),
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      onDone(result.id);
    } catch (err) {
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Use template: ${template.name}`} onClose={onClose}>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Recipients</label>
          <div className="mt-2 space-y-2">
            {recipients.map((r, i) => (
              <div key={r.role_id} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-700">{r.role_label}</span>
                  {roles[i].role_kind !== 'signer' && (
                    <span className="text-[10px] text-gray-400 uppercase">{roles[i].role_kind}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={r.name}
                    onChange={(e) => handleChange(i, 'name', e.target.value)}
                    placeholder="Full name"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="email"
                    value={r.email}
                    onChange={(e) => handleChange(i, 'email', e.target.value)}
                    placeholder="email@example.com"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">Message (optional)</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Add a short message for signers (optional)"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button
          onClick={onClose}
          disabled={saving}
          className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Create draft
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Bulk Send modal ────────────────────────────────────────────────────────

function BulkSendModal({ template, roles, onClose, onDone, onError }) {
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState([]); // [{ mappings: {role_id: {name, email}} }]
  const [rowErrors, setRowErrors] = useState([]); // [{row: number, role: string, error: string}]
  const [saving, setSaving] = useState(false);
  const [sendNow, setSendNow] = useState(true);
  const [stats, setStats] = useState({ valid: 0, invalid: 0 });
  const fileInputRef = useRef(null);

  const sampleCsv = useMemo(() => {
    const headers = [];
    for (const r of roles) {
      headers.push(`${r.role_label} Name`, `${r.role_label} Email`);
    }
    const exampleRow = roles.map(() => ['John Doe', 'john@example.com']).flat();
    return [headers.join(','), exampleRow.join(',')].join('\n');
  }, [roles]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result || '');
    reader.readAsText(file);
  };

  // Parse CSV whenever text changes
  useEffect(() => {
    if (!csvText.trim()) {
      setRows([]);
      setRowErrors([]);
      setStats({ valid: 0, invalid: 0 });
      return;
    }
    const { rows: parsed, errors } = parseCsv(csvText, roles);
    setRows(parsed);
    setRowErrors(errors);
    setStats({
      valid: parsed.length - new Set(errors.map((e) => e.row)).size,
      invalid: new Set(errors.map((e) => e.row)).size,
    });
  }, [csvText, roles]);

  const handleSubmit = async () => {
    if (rows.length === 0 || rowErrors.length > 0) return;
    setSaving(true);
    try {
      const result = await api.bulkSendTemplate(template.id, { rows, sendNow });
      onDone(result);
    } catch (err) {
      // Show server-side row errors if they came back
      if (err.body?.errors && Array.isArray(err.body.errors)) {
        setRowErrors(err.body.errors);
      }
      onError(err.message);
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`Bulk send: ${template.name}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
          Upload a CSV with one row per envelope. Columns required per role:{' '}
          <code className="bg-white/60 px-1 rounded">Name, Email</code>. Max 50 rows per batch.
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="border border-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Upload CSV
          </button>
          <button
            onClick={() => setCsvText(sampleCsv)}
            className="text-xs text-indigo-600 hover:text-indigo-700 underline-offset-2 hover:underline"
          >
            Insert sample
          </button>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 uppercase tracking-wide">
            CSV contents
          </label>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={8}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Paste CSV or upload a file"
          />
        </div>

        {rows.length > 0 && (
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1 text-left text-gray-600 w-8">#</th>
                  {roles.map((role) => (
                    <th key={role.id} className="px-2 py-1 text-left text-gray-600">
                      {role.role_label}
                    </th>
                  ))}
                  <th className="px-2 py-1 text-left text-gray-600 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const rowHasError = rowErrors.some((e) => e.row === i);
                  return (
                    <tr key={i} className={rowHasError ? 'bg-red-50' : ''}>
                      <td className="px-2 py-1 text-gray-400">{i + 1}</td>
                      {roles.map((role) => {
                        const m = row.mappings[role.id] || {};
                        const err = rowErrors.find((e) => e.row === i && e.role === role.role_label);
                        return (
                          <td
                            key={role.id}
                            className="px-2 py-1"
                            title={err ? err.error : undefined}
                          >
                            {m.name && m.email ? (
                              <span>
                                <span className="font-medium text-gray-800">{m.name}</span>{' '}
                                <span className="text-gray-500">&lt;{m.email}&gt;</span>
                              </span>
                            ) : (
                              <span className="text-red-600">{err?.error || 'Missing'}</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1">
                        {rowHasError ? (
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                        ) : (
                          <Check className="w-3.5 h-3.5 text-green-500" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rowErrors.length > 0 && (
          <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            {rowErrors.length} row-level error{rowErrors.length === 1 ? '' : 's'}. Fix the CSV and
            re-upload to proceed.
          </div>
        )}

        {rows.length > 0 && rowErrors.length === 0 && (
          <div className="text-xs text-gray-500">
            {stats.valid} valid row{stats.valid === 1 ? '' : 's'} — will create {stats.valid}{' '}
            envelope{stats.valid === 1 ? '' : 's'}.
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sendNow}
            onChange={(e) => setSendNow(e.target.checked)}
            className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
          Send immediately (uncheck to just create drafts for review)
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button
          onClick={onClose}
          disabled={saving}
          className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={rows.length === 0 || rowErrors.length > 0 || saving}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center gap-2 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {sendNow ? `Send ${rows.length || ''} envelope${rows.length === 1 ? '' : 's'}` : 'Create drafts'}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Confirm delete modal ───────────────────────────────────────────────────

function ConfirmDeleteModal({ name, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">Delete template?</h3>
            <p className="text-sm text-gray-600">
              Permanently deletes "<strong>{name}</strong>" and its role configuration. Envelopes
              already created from this template are unaffected.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onCancel}
            disabled={busy}
            className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Yes, delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── New Template modal — pick an existing envelope, name it, save it ──────

function NewTemplateModal({ onClose, onCreated, onError, onGoToEnvelopes }) {
  const [envelopes, setEnvelopes] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Load envelopes once on mount. We ask for a wide net — any status — so
  // drafts and completed envelopes both show up (templates can be made from
  // either).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listEnvelopes({ limit: 50, counts: false });
        if (cancelled) return;
        // Templates need at least one recipient on the source envelope, so
        // filter out envelopes with zero recipients up front.
        const usable = (data.items || []).filter((e) => (e.recipients?.length ?? 0) > 0);
        setEnvelopes(usable);
      } catch (err) {
        if (!cancelled) setLoadError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!envelopes) return [];
    const q = query.trim().toLowerCase();
    if (!q) return envelopes;
    return envelopes.filter(
      (e) =>
        (e.subject || '').toLowerCase().includes(q) ||
        (e.original_filename || '').toLowerCase().includes(q)
    );
  }, [envelopes, query]);

  const handlePick = (env) => {
    setSelected(env);
    setName((prev) => prev || env.subject || 'Untitled template');
  };

  const handleSave = async () => {
    if (!selected || !name.trim()) return;
    setSaving(true);
    try {
      await api.createTemplateFromEnvelope({
        sourceEnvelopeId: selected.id,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      onCreated(name.trim());
    } catch (err) {
      onError(err.body?.code === 'TEMPLATE_CAP_REACHED' ? err.message : err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-xl w-full p-6 shadow-xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">New template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!selected ? (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Pick an existing envelope to turn into a reusable template. Recipient emails are
              stripped — only names and the field layout are saved.
            </p>

            {envelopes === null && !loadError && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" />
              </div>
            )}

            {loadError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {loadError}
              </div>
            )}

            {envelopes && envelopes.length === 0 && (
              <div className="p-6 text-center border border-dashed border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600 mb-3">
                  You don't have any envelopes with recipients yet.
                </p>
                <button
                  onClick={onGoToEnvelopes}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm"
                >
                  Create an envelope first
                </button>
              </div>
            )}

            {envelopes && envelopes.length > 0 && (
              <>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by subject or filename…"
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-72 overflow-auto">
                  {filtered.length === 0 && (
                    <li className="p-4 text-sm text-gray-500 text-center">
                      No envelopes match "{query}".
                    </li>
                  )}
                  {filtered.map((env) => (
                    <li key={env.id}>
                      <button
                        type="button"
                        onClick={() => handlePick(env)}
                        className="w-full text-left p-3 hover:bg-gray-50 transition flex items-start gap-3"
                      >
                        <FileText className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {env.subject || 'Untitled'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {env.original_filename} · {env.recipient_count ?? env.recipients?.length ?? 0} recipient
                            {((env.recipient_count ?? env.recipients?.length ?? 0) === 1) ? '' : 's'} · {env.status}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="flex justify-end mt-5">
              <button
                onClick={onClose}
                className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg flex items-start gap-3">
              <FileText className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {selected.subject || 'Untitled'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {selected.original_filename} · {selected.recipient_count ?? selected.recipients?.length ?? 0} recipient
                  {((selected.recipient_count ?? selected.recipients?.length ?? 0) === 1) ? '' : 's'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-xs text-indigo-600 hover:text-indigo-700"
              >
                Change
              </button>
            </div>

            <label className="block mb-3">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                Template name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={128}
                autoFocus
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g. Onboarding Packet"
              />
            </label>

            <label className="block mb-4">
              <span className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                Description (optional)
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Internal notes about when to use this template"
              />
            </label>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSelected(null)}
                disabled={saving}
                className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={!name.trim() || saving}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save template
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Shared modal shell ─────────────────────────────────────────────────────

function ModalShell({ title, onClose, children, size = 'md' }) {
  const maxWidth = size === 'lg' ? 'max-w-2xl' : 'max-w-md';
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className={`bg-white rounded-xl ${maxWidth} w-full p-6 shadow-xl max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 transition p-1 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── CSV parser ─────────────────────────────────────────────────────────────

/**
 * Parse a CSV (RFC 4180-ish — handles quoted fields with embedded commas and
 * escaped double-quotes) and map columns to template roles.
 *
 * Header convention: `<role_label> Name` + `<role_label> Email` (case-insensitive).
 * Role matching is loose — column matches role whose label appears in the
 * header and who has "name"/"email" after it.
 */
function parseCsv(text, roles) {
  const lines = splitCsvLines(text.trim());
  if (lines.length < 2) return { rows: [], errors: [{ row: 0, error: 'CSV must have a header row + at least one data row.' }] };

  const header = parseCsvLine(lines[0]).map((h) => h.trim());

  // For each role, find the name + email column indexes
  const roleColumns = roles.map((role) => {
    const label = role.role_label.toLowerCase();
    const nameIdx = header.findIndex((h) => {
      const low = h.toLowerCase();
      return low.includes(label) && low.includes('name');
    });
    const emailIdx = header.findIndex((h) => {
      const low = h.toLowerCase();
      return low.includes(label) && low.includes('email');
    });
    return { role, nameIdx, emailIdx };
  });

  const errors = [];
  const missingColumns = roleColumns.filter((c) => c.nameIdx < 0 || c.emailIdx < 0);
  if (missingColumns.length > 0) {
    for (const m of missingColumns) {
      errors.push({
        row: 0,
        role: m.role.role_label,
        error: `Missing "${m.role.role_label} Name" or "${m.role.role_label} Email" column.`,
      });
    }
    return { rows: [], errors };
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i];
    if (!rawLine.trim()) continue;
    const cols = parseCsvLine(rawLine);
    const mappings = {};
    for (const { role, nameIdx, emailIdx } of roleColumns) {
      const name = (cols[nameIdx] || '').trim();
      const email = (cols[emailIdx] || '').trim();
      mappings[role.id] = { name, email };
      if (!name) {
        errors.push({ row: rows.length, role: role.role_label, error: 'Name is required.' });
      }
      if (!email || !EMAIL_RE.test(email)) {
        errors.push({
          row: rows.length,
          role: role.role_label,
          error: `Invalid email: ${email || '(empty)'}`,
        });
      }
    }
    rows.push({ mappings });
  }
  return { rows, errors };
}

function splitCsvLines(text) {
  // Respects quoted newlines — walks the string once.
  const out = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      // Escaped quote?
      if (inQuote && text[i + 1] === '"') {
        cur += '""';
        i += 1;
      } else {
        inQuote = !inQuote;
        cur += c;
      }
    } else if ((c === '\n' || c === '\r') && !inQuote) {
      if (cur.length > 0) out.push(cur);
      cur = '';
      // Skip \r\n pairs
      if (c === '\r' && text[i + 1] === '\n') i += 1;
    } else {
      cur += c;
    }
  }
  if (cur.length > 0) out.push(cur);
  return out;
}

function parseCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuote && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuote = !inQuote;
      }
    } else if (c === ',' && !inQuote) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}
